import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BlogItem, fetchBlogByNav } from './blogApi';
import { CourseSummary, fetchCoursesCached } from './courseApi';

const stripHtml = (html: string) => {
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]*>/g, ' ');
  }

  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

const BlogArticle: React.FC = () => {
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();

  const [blog, setBlog] = useState<BlogItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendedCourses, setRecommendedCourses] = useState<CourseSummary[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        setError(null);

        const match = await fetchBlogByNav(slug.toLowerCase().trim());

        if (!match) {
          setNotFound(true);
          setBlog(null);
        } else {
          setBlog(match);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load article');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const items = await fetchCoursesCached();
        const normalized = items
          .filter((course) => course && typeof course.id === 'number' && typeof course.name === 'string')
          .slice(0, 6);

        setRecommendedCourses(normalized);
      } catch {
        setRecommendedCourses([]);
      }
    };

    loadCourses();
  }, []);

  // Set page title for SEO
  useEffect(() => {
    if (blog) {
      document.title = `${blog.pageTitle || blog.infoName} | Exam Masters`;
    } else {
      document.title = 'Blog | Exam Masters';
    }

    return () => {
      document.title = 'Exam Masters';
    };
  }, [blog]);

  return (
    <div
      className="bg-gradient-to-br from-app-page-from to-app-page-to"
      style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      {/* Breadcrumb */}
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
          <span className="app-breadcrumb-sep">›</span>
          <Link to="/blogs" className="app-breadcrumb-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Blogs</Link>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">{blog?.infoName ?? 'Article'}</span>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3 pb-6 md:p-6">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 items-start">
          <div className="lg:col-span-8">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">

          {/* Header bar */}
          <div className="px-4 py-3 md:px-6 md:py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <div className="mb-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/blogs')}
                className="inline-flex items-center justify-center rounded-lg border border-white/30 bg-white/10 px-2.5 py-2 text-white transition-colors hover:bg-white/15"
                aria-label="Back to blogs"
                title="Back to blogs"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 20 20"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                </svg>
              </button>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/75 text-left">Exam Masters · Blog</p>
            </div>
            {blog && (
              <>
                <h1 className="mt-1 text-xl md:text-2xl font-bold leading-snug text-left">{blog.infoName}</h1>
                {blog.infoHeader && (
                  <p className="mt-1 text-sm text-white/85 text-left">{blog.infoHeader}</p>
                )}
                <p className="mt-1 text-xs text-white/70 text-left">
                  {new Date(blog.createdDate).toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })}
                </p>
              </>
            )}
          </div>

          <div className="p-4 md:p-6 md:p-8">
            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center min-h-[200px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500">Loading article...</p>
                </div>
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-left">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  type="button"
                  onClick={() => navigate('/blogs')}
                  className="mt-3 inline-flex items-center rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                >
                  ← Back to blogs
                </button>
              </div>
            )}

            {/* Not found */}
            {!loading && !error && notFound && (
              <div className="text-center py-12">
                <div className="text-5xl mb-4">🔍</div>
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Article not found</h2>
                <p className="text-sm text-gray-500 mb-4">The article you're looking for may have moved or been removed.</p>
                <button
                  type="button"
                  onClick={() => navigate('/blogs')}
                  className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  Browse all blogs
                </button>
              </div>
            )}

            {/* Article content */}
            {!loading && !error && blog && (
              <>
                <style>{`
                  .blog-content {
                    text-align: left !important;
                  }
                  .blog-content h1, .blog-content h2, .blog-content h3,
                  .blog-content h4, .blog-content h5, .blog-content h6,
                  .blog-content p,
                  .blog-content li,
                  .blog-content dt,
                  .blog-content dd,
                  .blog-content blockquote {
                    text-align: left !important;
                  }
                  .blog-content ul {
                    margin: 16px 0 !important;
                    padding-left: 24px !important;
                    list-style-type: disc !important;
                  }
                  .blog-content ul ul {
                    margin: 8px 0 !important;
                    padding-left: 20px !important;
                    list-style-type: circle !important;
                  }
                  .blog-content ol {
                    margin: 16px 0 !important;
                    padding-left: 24px !important;
                    list-style-type: decimal !important;
                  }
                  .blog-content li {
                    margin: 8px 0 !important;
                    padding-left: 4px !important;
                    line-height: 1.6 !important;
                  }
                  .blog-content li p {
                    margin: 4px 0 !important;
                  }
                  .blog-content dl {
                    margin: 20px 0 !important;
                    padding: 16px !important;
                    background-color: #f8f9fa !important;
                    border-radius: 8px !important;
                    border-left: 4px solid #2c5aa0 !important;
                  }
                  .blog-content dt {
                    font-weight: 600 !important;
                    color: #2c5aa0 !important;
                    margin: 12px 0 4px 0 !important;
                    font-size: 1rem !important;
                    display: inline-block !important;
                    min-width: 120px !important;
                  }
                  .blog-content dd {
                    margin: 0 0 12px 0 !important;
                    padding-left: 8px !important;
                    color: #333 !important;
                    font-size: 1rem !important;
                    display: inline-block !important;
                    margin-left: 8px !important;
                  }
                  .blog-content dt:first-of-type {
                    margin-top: 0 !important;
                  }
                  .blog-content dd:last-of-type {
                    margin-bottom: 0 !important;
                  }
                  .blog-content h1, .blog-content h2, .blog-content h3,
                  .blog-content h4, .blog-content h5, .blog-content h6 {
                    margin: 24px 0 16px 0 !important;
                    font-weight: 600 !important;
                    color: #2c5aa0 !important;
                  }
                  .blog-content p {
                    margin: 12px 0 !important;
                    line-height: 1.7 !important;
                    color: #374151 !important;
                  }
                  .blog-content blockquote {
                    margin: 16px 0 !important;
                    padding: 16px 20px !important;
                    border-left: 4px solid #2c5aa0 !important;
                    background-color: #f8f9fa !important;
                    font-style: italic !important;
                  }
                  .blog-content code {
                    background-color: #f1f1f1 !important;
                    padding: 2px 6px !important;
                    border-radius: 4px !important;
                    font-family: 'Courier New', monospace !important;
                    font-size: 0.9em !important;
                  }
                  .blog-content pre {
                    background-color: #f8f9fa !important;
                    padding: 16px !important;
                    border-radius: 8px !important;
                    overflow-x: auto !important;
                    margin: 16px 0 !important;
                  }
                  .blog-content a {
                    color: #2c5aa0 !important;
                    text-decoration: underline !important;
                  }
                  .blog-content a:hover {
                    color: #1e3d72 !important;
                  }
                  .blog-content strong, .blog-content b {
                    font-weight: 600 !important;
                  }
                  .blog-content em, .blog-content i {
                    font-style: italic !important;
                  }
                  .blog-content table {
                    width: 100% !important;
                    border-collapse: collapse !important;
                    margin: 16px 0 !important;
                    background-color: #fff !important;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
                  }
                  .blog-content th, .blog-content td {
                    padding: 12px !important;
                    text-align: left !important;
                    border-bottom: 1px solid #e0e0e0 !important;
                  }
                  .blog-content th {
                    background-color: #f8f9fa !important;
                    font-weight: 600 !important;
                    color: #2c5aa0 !important;
                  }
                  .blog-content tr:hover {
                    background-color: #f8f9fa !important;
                  }
                `}</style>
                <div
                  className="blog-content text-left"
                  dangerouslySetInnerHTML={{ __html: blog.inforDetails }}
                />

                {/* Tags */}
                {blog.tags && (
                  <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Related Topics</p>
                    <div className="flex flex-wrap gap-2">
                      {blog.tags.split(',').map((tag, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white"
                        >
                          #{tag.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="mt-8 rounded-lg border border-indigo-200 bg-indigo-50 p-4 md:p-6 text-left">
                  <h3 className="text-base font-bold text-indigo-900 mb-1">Ready to practise?</h3>
                  <p className="text-sm text-indigo-700 mb-3">Try real exam-style maths questions with instant explanations designed for VIC / NSW / SA / WA.</p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/practice-tests')}
                      className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                    >
                      View Practice Tests
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/courses')}
                      className="inline-flex items-center rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                    >
                      Browse Courses
                    </button>
                  </div>
                </div>

                {/* Back link */}
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => navigate('/blogs')}
                    className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    ← Back to all blogs
                  </button>
                </div>
              </>
            )}
            </div>
          </div>
          </div>

          <aside className="hidden lg:block lg:col-span-4">
            <div className="sticky top-6 space-y-4">
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-slate-100 to-slate-50">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Quick Actions</p>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5">Practice Now</p>
                </div>
                <div className="p-4 space-y-3">
                  <button
                    type="button"
                    onClick={() => navigate('/practice')}
                    className="w-full rounded-xl border border-blue-200 bg-blue-50 p-4 text-left transition-colors hover:bg-blue-100"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-blue-900">Practice Questions</p>
                        <p className="mt-1 text-xs text-blue-700">Solve topic-wise questions with instant explanations.</p>
                      </div>
                      <span className="text-blue-700 text-lg font-bold">→</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate('/practice-tests')}
                    className="w-full rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-left transition-colors hover:bg-indigo-100"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-indigo-900">Exam Practice</p>
                        <p className="mt-1 text-xs text-indigo-700">Take timed tests and track your progress.</p>
                      </div>
                      <span className="text-indigo-700 text-lg font-bold">→</span>
                    </div>
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-slate-100 to-slate-50">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Recommended</p>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5">Courses</p>
                </div>
                <div className="p-4 space-y-3">
                  {recommendedCourses.length > 0 ? (
                    recommendedCourses.map((course) => (
                      <button
                        key={course.id}
                        type="button"
                        onClick={() => navigate(`/course-details/${(course.inforPagenav ?? '').trim() || course.id}`)}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-left transition-colors hover:bg-slate-100"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{course.name}</p>
                            {course.difficultyLevel && (
                              <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                {course.difficultyLevel}
                              </p>
                            )}
                            {course.description && (
                              <p className="mt-1 text-xs text-slate-600 line-clamp-2">{stripHtml(course.description)}</p>
                            )}
                          </div>
                          <span className="text-slate-500 text-base font-bold">→</span>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-sm text-slate-600 text-left">No courses available right now.</p>
                      <button
                        type="button"
                        onClick={() => navigate('/courses')}
                        className="mt-2 inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                      >
                        Browse Courses
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BlogArticle;
